import React from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { cartStockLimit } from "@/lib/cartInventory";
import { inr } from "@/lib/api";
import { FoodSymbol } from "@/components/FoodSymbol";

const CartDrawer = () => {
  const { items, open, setOpen, remove, setQty, subtotal } = useCart();
  return (
    <div className={`fixed inset-0 z-[60] overflow-hidden ${open ? "pointer-events-auto" : "hidden"}`}>
      <div className={`absolute inset-0 bg-black/40 transition-opacity ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
      <div className={`absolute right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl transition-transform flex flex-col ${open ? "translate-x-0" : "translate-x-full"}`} data-testid="cart-drawer">
        <div className="flex items-center justify-between p-5 border-b border-[#E2DCD2]">
          <div className="font-serif text-2xl">Your Bag</div>
          <button onClick={() => setOpen(false)} data-testid="close-cart"><X /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>Your bag is empty.</p>
              <Link to="/shop" onClick={() => setOpen(false)} className="inline-block mt-4 text-[#C86D42] font-medium">Browse products →</Link>
            </div>
          ) : (
            <div className="divide-y divide-[#E2DCD2]">
              {items.map((i) => (
                <div key={i._k} className="p-4 flex gap-3">
                  <img src={i.image} alt="" className="w-20 h-20 rounded-lg object-cover bg-[#F4F1EA]" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-1 text-sm font-semibold line-clamp-2">
                      <FoodSymbol
                        value={i.food_symbol}
                        className="mt-0.5"
                        testId={`cart-food-symbol-${i._k}`}
                      />
                      <span>{i.name}</span>
                    </div>
                    {i.variant_label && <div className="text-xs text-gray-500">{i.variant_label}</div>}
                    <div className="text-sm font-bold text-[#2D5A47] mt-1">{inr(i.price)}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <div className="flex items-center border border-[#E2DCD2] rounded-lg">
                        <button type="button" onClick={() => setQty(i._k, i.quantity - 1)} className="p-1.5" aria-label={`Decrease ${i.name}`} data-testid={`drawer-decrease-${i._k}`}><Minus size={12} /></button>
                        <input type="number" min="1" max={cartStockLimit(i, items)} step="1" value={i.quantity} onChange={(event) => { if (event.target.value !== "") setQty(i._k, Number(event.target.value)); }} aria-label={`Quantity for ${i.name}`} className="w-14 text-center text-sm" data-testid={`drawer-quantity-${i._k}`} />
                        <button type="button" disabled={i.quantity >= cartStockLimit(i, items)} onClick={() => setQty(i._k, i.quantity + 1)} className="p-1.5 disabled:opacity-40" aria-label={`Increase ${i.name}`} data-testid={`drawer-increase-${i._k}`}><Plus size={12} /></button>
                      </div>
                      <button
                        type="button"
                        onClick={() => remove(i._k)}
                        className="text-gray-400 hover:text-[#C86D42]"
                        data-testid={`cart-remove-${i._k}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {items.length > 0 && (
          <div className="p-5 border-t border-[#E2DCD2] bg-[#FDFBF7]">
            <div className="flex justify-between text-sm mb-2"><span>Subtotal</span><span className="font-semibold">{inr(subtotal)}</span></div>
            <div className="text-xs text-gray-500 mb-4">Shipping & taxes calculated at checkout.</div>
            <Link to="/checkout" onClick={() => setOpen(false)} className="block text-center bg-[#C86D42] hover:bg-[#AE5931] text-white font-semibold py-3 rounded-lg" data-testid="checkout-btn">
              Proceed to Checkout →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
